import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    Fab,
    IconButton,
    Popover,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AudioFileOutlinedIcon from '@mui/icons-material/AudioFileOutlined';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { CampaignThreadAttachmentGrid } from './CampaignThreadAttachmentGrid';
import { CampaignThreadMessageContent } from './CampaignThreadMessageContent';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type {
    IWorkspaceMessage,
    IWorkspaceContentSubmission,
    ISubmissionProject,
    WorkspaceMessageType,
} from '../../../redux/buzzbridge/campaignWorkspace.types';
import {
    sendCampaignThreadMessage,
    submitParticipantWork,
    retrySubmissionProcessing,
} from '../../../redux/buzzbridge/actions';
import {
    buzzBridgeProfileSelector,
    campaignThreadMessageErrorSelector,
    campaignWorkspaceSelector,
    sendingCampaignThreadMessageSelector,
    retryingSubmissionProcessingSelector,
} from '../../../redux/buzzbridge/selectors';
import { userProfileSelector } from '../../../selectors/selectors';
import { canSendCampaignThreadMessage } from '../../../utils/campaignWorkspaceAccess';
import { campaignThreadTypingReceived } from '../../../redux/buzzbridge/actions';
import { useCampaignThreadTypingEmitter } from '../../../hook/useCampaignThreadSocketRoom';
import { useCampaignThreadRemoteTyping } from '../../../hook/useCampaignThreadRemoteTyping';
import { CampaignThreadTypingBar } from './CampaignThreadTypingBar';
import { useCampaignThreadMessageScroll } from '../../../hook/useCampaignThreadMessageScroll';
import { DEFAULT_CAMPAIGN_THREAD_LOCATION_LABEL } from '../../../utils/campaignThreadLocation';
import { compressImageForUpload } from '../../../utils/compressImageForUpload';
import { SectionLabel } from '../BrandCampaignDetailPage';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_OUTLINE_VARIANT,
} from '../brandDashboardTokens';

const CREATOR_GOLD = '#ffc965';
const CREATOR_ON_GOLD = '#392700';
const CREATOR_SURFACE = '#1a1919';
const ON_VARIANT_SOFT = 'rgba(255,255,255,0.45)';

export type WorkspaceDiscussionVariant = 'brand' | 'creator';

interface PendingFile {
    file: File;
    kind: 'image' | 'video' | 'document' | 'audio';
    objectUrl: string;
}

function formatTimeBrand(iso?: string) {
    if (!iso) return '';
    const d = new Date(iso);
    return (
        d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
        ' · ' +
        d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    );
}

function formatTimeCreator(iso?: string) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function senderLabel(m: IWorkspaceMessage): string {
    if (m.sender.display_name?.trim()) return m.sender.display_name.trim();
    const fn = m.sender.firstName || '';
    const ln = m.sender.lastName || '';
    const full = `${fn} ${ln}`.trim();
    if (full) return full;
    if (m.sender.userName) return m.sender.userName;
    return 'Participant';
}

function fileKind(file: File): PendingFile['kind'] {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    return 'document';
}

export function ParticipantWorkspaceDiscussion({
    participantId,
    campaignId,
    messageType,
    messages,
    creatorDisplayName,
    counterpartDisplayName,
    counterpartUserIds = [],
    currentUserId,
    hasMoreOlder,
    loadingOlder,
    onLoadOlder,
    variant = 'brand',
    contentSubmissions = [],
    participationBlocked = false,
}: {
    participantId: string;
    campaignId?: string;
    messageType: WorkspaceMessageType;
    messages: IWorkspaceMessage[];
    creatorDisplayName: string;
    /** Name shown in typing indicator when socket payload has no displayName (usually the other party). */
    counterpartDisplayName?: string;
    /** User/influencer ids for the other party — used to detect mis-tagged typing payloads. */
    counterpartUserIds?: string[];
    currentUserId?: string;
    hasMoreOlder: boolean;
    loadingOlder: boolean;
    onLoadOlder: () => void;
    variant?: WorkspaceDiscussionVariant;
    contentSubmissions?: IWorkspaceContentSubmission[];
    /** Block new messages when campaign is closed (creator + brand) or participation_gate blocks creator */
    participationBlocked?: boolean;
}) {
    const dispatch = useDispatch();
    const sending = useSelector(sendingCampaignThreadMessageSelector);
    const threadError = useSelector(campaignThreadMessageErrorSelector);
    const workspace = useSelector(campaignWorkspaceSelector);
    const userProfile = useSelector(userProfileSelector);
    const buzzProfile = useSelector(buzzBridgeProfileSelector);
    const belongsToWorkspace = canSendCampaignThreadMessage(workspace, {
        currentUserId: currentUserId ?? userProfile?._id,
        currentUserRole: userProfile?.role,
        currentInfluencerId: buzzProfile?.data?.influencer?._id,
    });
    const pid = participantId?.trim() ?? '';
    const lastMessageKey = useMemo(() => {
        const last = messages[messages.length - 1];
        return last?._id ?? last?.createdAt ?? String(messages.length);
    }, [messages]);
    const { scrollRef, pendingNewCount, scrollToLatest, handleScroll } = useCampaignThreadMessageScroll(
        messages.length,
        lastMessageKey,
    );
    const [draft, setDraft] = useState('');
    const { remoteTyping, showRemoteTyping } = useCampaignThreadRemoteTyping(
        pid,
        currentUserId,
        counterpartDisplayName,
        counterpartUserIds,
        variant === 'brand',
        draft.length > 0,
    );
    const typingLabel = `${
        remoteTyping?.displayName?.trim() || counterpartDisplayName?.trim() || creatorDisplayName?.trim() || 'Someone'
    } is typing…`;
    const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
    const [compressingFiles, setCompressingFiles] = useState(false);
    const [pendingLocation, setPendingLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [locating, setLocating] = useState(false);
    const [attachAnchor, setAttachAnchor] = useState<HTMLButtonElement | null>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const docInputRef = useRef<HTMLInputElement>(null);
    const audioInputRef = useRef<HTMLInputElement>(null);
    const composerInputRef = useRef<HTMLInputElement>(null);
    const wasSendingRef = useRef(false);

    // revoke object URLs on unmount
    useEffect(() => {
        return () => {
            pendingFiles.forEach(pf => URL.revokeObjectURL(pf.objectUrl));
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const addFiles = useCallback(async (files: FileList | null) => {
        if (!files?.length) return;
        setCompressingFiles(true);
        try {
            const added: PendingFile[] = [];
            for (const file of Array.from(files)) {
                const kind = fileKind(file);
                if (kind === 'image' && file.type.startsWith('image/')) {
                    const compressed = await compressImageForUpload(file);
                    added.push({
                        file: compressed,
                        kind,
                        objectUrl: URL.createObjectURL(compressed),
                    });
                } else {
                    added.push({
                        file,
                        kind,
                        objectUrl: URL.createObjectURL(file),
                    });
                }
            }
            setPendingFiles(prev => [...prev, ...added]);
        } finally {
            setCompressingFiles(false);
        }
    }, []);

    const removeFile = useCallback((idx: number) => {
        setPendingFiles(prev => {
            URL.revokeObjectURL(prev[idx].objectUrl);
            return prev.filter((_, i) => i !== idx);
        });
    }, []);

    const handleLocate = useCallback(() => {
        if (!navigator.geolocation) return;
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            pos => {
                setPendingLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                setLocating(false);
                setAttachAnchor(null);
            },
            () => setLocating(false),
        );
    }, []);

    const clearLocation = useCallback(() => setPendingLocation(null), []);

    const composerDisabled = participationBlocked || (Boolean(workspace) && !belongsToWorkspace);
    const emitTyping = useCampaignThreadTypingEmitter(pid, Boolean(pid) && !composerDisabled);
    const canSend = Boolean(
        !composerDisabled &&
            belongsToWorkspace &&
            !sending &&
            !compressingFiles &&
            participantId &&
            (draft.trim() || pendingFiles.length > 0 || pendingLocation),
    );

    const send = useCallback(() => {
        if (!canSend || composerDisabled || !belongsToWorkspace) return;
        const content = draft.trim();
        const locations = pendingLocation
            ? [{ lat: pendingLocation.lat, lng: pendingLocation.lng, label: DEFAULT_CAMPAIGN_THREAD_LOCATION_LABEL }]
            : undefined;
        dispatch(
            sendCampaignThreadMessage({
                participantId,
                content,
                messageType,
                files: pendingFiles.map(pf => pf.file),
                locations,
            }),
        );
        setDraft('');
        emitTyping(false);
        if (pid) {
            dispatch(campaignThreadTypingReceived({ participantId: pid, typing: null }));
        }
        setPendingFiles(prev => {
            prev.forEach(pf => URL.revokeObjectURL(pf.objectUrl));
            return [];
        });
        setPendingLocation(null);
        // Keep typing focus after send (Enter or send button click).
        requestAnimationFrame(() => composerInputRef.current?.focus());
    }, [
        belongsToWorkspace,
        canSend,
        composerDisabled,
        dispatch,
        draft,
        emitTyping,
        messageType,
        participantId,
        pendingFiles,
        pendingLocation,
        pid,
    ]);

    useEffect(() => {
        if (wasSendingRef.current && !sending && !composerDisabled) {
            composerInputRef.current?.focus();
        }
        wasSendingRef.current = sending;
    }, [sending, composerDisabled]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== 'Enter' || e.shiftKey) return;
        e.preventDefault();
        send();
    };

    const isCreator = variant === 'creator';
    const accentColor = isCreator ? CREATOR_GOLD : BRAND_LUXE_PRIMARY;

    const attachMenuOpen = Boolean(attachAnchor);

    const attachMenu = (
        <Popover
            open={attachMenuOpen}
            anchorEl={attachAnchor}
            onClose={() => setAttachAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{
                sx: {
                    bgcolor: '#1c1c1c',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: 2,
                    p: 0.5,
                    minWidth: 170,
                },
            }}>
            {[
                {
                    label: 'Photo / Image',
                    icon: <ImageOutlinedIcon sx={{ fontSize: 17 }} />,
                    action: () => {
                        imageInputRef.current?.click();
                        setAttachAnchor(null);
                    },
                },
                {
                    label: 'Video',
                    icon: <VideoFileOutlinedIcon sx={{ fontSize: 17 }} />,
                    action: () => {
                        imageInputRef.current?.click();
                        setAttachAnchor(null);
                    },
                },
                {
                    label: 'Document',
                    icon: <InsertDriveFileOutlinedIcon sx={{ fontSize: 17 }} />,
                    action: () => {
                        docInputRef.current?.click();
                        setAttachAnchor(null);
                    },
                },
                {
                    label: 'Audio',
                    icon: <MusicNoteOutlinedIcon sx={{ fontSize: 17 }} />,
                    action: () => {
                        audioInputRef.current?.click();
                        setAttachAnchor(null);
                    },
                },
                {
                    label: locating ? 'Getting location…' : 'Location',
                    icon: locating ? (
                        <CircularProgress size={14} sx={{ color: accentColor }} />
                    ) : (
                        <LocationOnOutlinedIcon sx={{ fontSize: 17 }} />
                    ),
                    action: handleLocate,
                },
            ].map(item => (
                <Box
                    key={item.label}
                    onClick={item.action}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        color: 'rgba(255,255,255,0.75)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 13,
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' },
                        transition: 'all 0.12s',
                    }}>
                    {item.icon}
                    {item.label}
                </Box>
            ))}
        </Popover>
    );

    // Hidden file inputs
    const hiddenInputs = (
        <>
            <input
                ref={imageInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                style={{ display: 'none' }}
                onChange={e => addFiles(e.target.files)}
                onClick={e => {
                    (e.target as HTMLInputElement).value = '';
                }}
            />
            <input
                ref={docInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.csv,.zip"
                multiple
                style={{ display: 'none' }}
                onChange={e => addFiles(e.target.files)}
                onClick={e => {
                    (e.target as HTMLInputElement).value = '';
                }}
            />
            <input
                ref={audioInputRef}
                type="file"
                accept="audio/*"
                multiple
                style={{ display: 'none' }}
                onChange={e => addFiles(e.target.files)}
                onClick={e => {
                    (e.target as HTMLInputElement).value = '';
                }}
            />
        </>
    );

    // Preview strip for pending files + location
    const hasAttachments = pendingFiles.length > 0 || pendingLocation || compressingFiles;
    const previewStrip = hasAttachments ? (
        <Box
            sx={{
                px: isCreator ? 2 : 2,
                pt: 1,
                pb: 0.5,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                borderTop: `1px solid rgba(255,255,255,0.05)`,
            }}>
            {compressingFiles ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1 }}>
                    <CircularProgress size={16} sx={{ color: accentColor }} />
                    <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>Compressing…</Typography>
                </Box>
            ) : null}
            {pendingFiles.map((pf, idx) => (
                <Box
                    key={idx}
                    sx={{
                        position: 'relative',
                        width: pf.kind === 'image' ? 60 : 'auto',
                        height: pf.kind === 'image' ? 60 : 'auto',
                        minWidth: pf.kind !== 'image' ? 120 : undefined,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: pf.kind !== 'image' ? 1.5 : 0,
                        border: '1px solid rgba(255,255,255,0.08)',
                    }}>
                    {pf.kind === 'image' ? (
                        <Box
                            component="img"
                            src={pf.objectUrl}
                            alt=""
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <>
                            {pf.kind === 'video' && (
                                <PlayCircleOutlineIcon sx={{ fontSize: 16, color: accentColor, flexShrink: 0 }} />
                            )}
                            {pf.kind === 'document' && (
                                <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: accentColor, flexShrink: 0 }} />
                            )}
                            {pf.kind === 'audio' && (
                                <AudioFileOutlinedIcon sx={{ fontSize: 16, color: accentColor, flexShrink: 0 }} />
                            )}
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: 'rgba(255,255,255,0.75)',
                                    maxWidth: 100,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                {pf.file.name}
                            </Typography>
                        </>
                    )}
                    <IconButton
                        size="small"
                        onClick={() => removeFile(idx)}
                        sx={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            width: 18,
                            height: 18,
                            bgcolor: '#333',
                            '&:hover': { bgcolor: '#555' },
                            p: 0,
                        }}>
                        <CloseIcon sx={{ fontSize: 11, color: '#fff' }} />
                    </IconButton>
                </Box>
            ))}
            {pendingLocation ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        borderRadius: 1.5,
                        border: '1px solid rgba(255,255,255,0.08)',
                        position: 'relative',
                    }}>
                    <LocationOnOutlinedIcon sx={{ fontSize: 15, color: accentColor }} />
                    <Typography
                        sx={{ fontSize: 11, fontFamily: FONT_ENUM.MAIN_MEDIUM, color: 'rgba(255,255,255,0.75)' }}>
                        {pendingLocation.lat.toFixed(4)}, {pendingLocation.lng.toFixed(4)}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={clearLocation}
                        sx={{ width: 18, height: 18, bgcolor: '#333', '&:hover': { bgcolor: '#555' }, p: 0, ml: 0.5 }}>
                        <CloseIcon sx={{ fontSize: 11, color: '#fff' }} />
                    </IconButton>
                </Box>
            ) : null}
        </Box>
    ) : null;

    return (
        <Box
            sx={{
                ...(isCreator
                    ? {
                          flex: '1 1 0%',
                          minHeight: 0,
                          minWidth: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          bgcolor: 'transparent',
                          overflow: 'hidden',
                      }
                    : {
                          bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                          borderRadius: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                      }),
            }}>
            {hiddenInputs}
            {attachMenu}

            {!isCreator ? (
                <Box sx={{ px: 3, pt: 3, pb: 1.5 }}>
                    <SectionLabel>Discussion</SectionLabel>
                </Box>
            ) : null}

            <Box sx={{ position: 'relative', flex: '1 1 0%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {pendingNewCount > 0 ? (
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            display: 'flex',
                            justifyContent: 'center',
                            pt: isCreator ? 1 : 0.5,
                            pb: 0.5,
                        }}>
                        <Chip
                            label={pendingNewCount > 1 ? `${pendingNewCount} new messages` : 'New message available'}
                            size="small"
                            onClick={() => scrollToLatest('smooth')}
                            sx={{
                                bgcolor: isCreator ? CREATOR_GOLD : BRAND_LUXE_PRIMARY,
                                color: isCreator ? CREATOR_ON_GOLD : '#1a1a1a',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.9 },
                            }}
                        />
                    </Box>
                ) : null}

                <Box
                    ref={scrollRef}
                    onScroll={handleScroll}
                    sx={{
                        flex: '1 1 0%',
                        minHeight: 0,
                        minWidth: 0,
                        px: isCreator ? { xs: 2.5, md: 2.5 } : 3,
                        pt: isCreator ? 1.5 : 1,
                        pb: isCreator ? 0.5 : 1,
                        ...(!isCreator ? { maxHeight: 420 } : null),
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': { width: 4 },
                        '&::-webkit-scrollbar-thumb': { bgcolor: '#262626', borderRadius: 10 },
                    }}>
                    <Box
                        sx={{
                            minHeight: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            gap: isCreator ? 1.5 : 1.25,
                        }}>
                        {hasMoreOlder ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                <Button
                                    size="small"
                                    disabled={loadingOlder}
                                    onClick={onLoadOlder}
                                    sx={{
                                        color: CREATOR_GOLD,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    }}>
                                    {loadingOlder ? (
                                        <CircularProgress size={18} color="inherit" />
                                    ) : (
                                        'Load earlier messages'
                                    )}
                                </Button>
                            </Box>
                        ) : null}
                        {messages.map((msg, idx) => (
                            <MessageBubble
                                key={msg._id ?? `${msg.createdAt}-${idx}`}
                                message={msg}
                                isMine={Boolean(currentUserId && msg.sender._id === currentUserId)}
                                variant={variant}
                                contentSubmissions={contentSubmissions}
                                participantId={participantId}
                                campaignId={campaignId}
                            />
                        ))}
                    </Box>
                </Box>

                {pendingNewCount > 0 ? (
                    <Fab
                        size="small"
                        aria-label="Jump to latest messages"
                        onClick={() => scrollToLatest('smooth')}
                        sx={{
                            position: 'absolute',
                            right: isCreator ? 16 : 20,
                            bottom: 8,
                            zIndex: 2,
                            bgcolor: isCreator ? CREATOR_GOLD : BRAND_LUXE_PRIMARY,
                            color: isCreator ? CREATOR_ON_GOLD : '#1a1a1a',
                            '&:hover': {
                                bgcolor: isCreator ? CREATOR_GOLD : BRAND_LUXE_PRIMARY,
                                opacity: 0.9,
                            },
                        }}>
                        <KeyboardArrowDownIcon />
                    </Fab>
                ) : null}
            </Box>

            {threadError ? (
                <Box sx={{ px: isCreator ? 2.5 : 3, pb: 1 }}>
                    <ThreadErrorAlert error={threadError} />
                </Box>
            ) : null}

            {/* Preview strip */}
            {previewStrip}

            {showRemoteTyping ? (
                <Box
                    sx={{
                        flexShrink: 0,
                        px: isCreator ? 2.5 : 3,
                        pt: 0.5,
                        pb: 0,
                        borderTop: isCreator ? '1px solid rgba(255,255,255,0.05)' : undefined,
                        bgcolor: isCreator ? CREATOR_SURFACE : BRAND_SURFACE_CONTAINER,
                    }}>
                    <CampaignThreadTypingBar
                        label={typingLabel}
                        accentColor={isCreator ? CREATOR_GOLD : BRAND_LUXE_PRIMARY}
                        mutedColor={isCreator ? ON_VARIANT_SOFT : 'rgba(255,255,255,0.55)'}
                    />
                </Box>
            ) : null}

            {isCreator ? (
                <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.05)', bgcolor: CREATOR_SURFACE }}>
                    {composerDisabled ? (
                        <Typography sx={{ fontSize: 12, color: ON_VARIANT_SOFT, mb: 1, px: 0.5 }}>
                            {workspace && !belongsToWorkspace
                                ? 'You do not have access to send messages in this workspace.'
                                : participationBlocked
                                ? 'This campaign is closed. You can still read the thread above, but you can no longer send messages or submit content.'
                                : 'Messaging is paused while this campaign is on hold. You can still read the thread above.'}
                        </Typography>
                    ) : null}
                    <Box
                        sx={{
                            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                            borderRadius: '12px',
                            px: 2,
                            py: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            opacity: composerDisabled ? 0.5 : 1,
                        }}>
                        <Tooltip
                            title={composerDisabled ? 'Messaging paused' : 'Attach file or location'}
                            placement="top">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={composerDisabled}
                                    onClick={e => setAttachAnchor(e.currentTarget)}
                                    sx={{
                                        color: attachMenuOpen ? accentColor : ON_VARIANT_SOFT,
                                        '&:hover': { color: accentColor },
                                    }}>
                                    <AttachFileOutlinedIcon sx={{ fontSize: 22 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={composerDisabled ? 'Messaging paused for this campaign' : 'Type your message…'}
                            value={draft}
                            onChange={e => {
                                const value = e.target.value;
                                setDraft(value);
                                emitTyping(value.length > 0);
                            }}
                            onKeyDown={handleKeyDown}
                            inputRef={composerInputRef}
                            disabled={!participantId || composerDisabled}
                            multiline
                            maxRows={4}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: 'transparent',
                                    borderRadius: 0,
                                    color: '#fff',
                                    fontSize: 14,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '& fieldset': { border: 'none' },
                                    '&:hover fieldset': { border: 'none' },
                                    '&.Mui-focused fieldset': { border: 'none' },
                                },
                                '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.35)', opacity: 1 },
                            }}
                        />
                        <IconButton size="small" disabled title="Coming soon" sx={{ color: ON_VARIANT_SOFT }}>
                            <SentimentSatisfiedAltOutlinedIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                        <IconButton
                            disabled={!canSend}
                            onClick={send}
                            aria-label="Send message"
                            sx={{
                                bgcolor: CREATOR_GOLD,
                                color: CREATOR_ON_GOLD,
                                borderRadius: '8px',
                                width: 32,
                                height: 32,
                                '&:hover': { bgcolor: '#feb700' },
                                '&.Mui-disabled': { bgcolor: 'rgba(255,201,101,0.2)', color: 'rgba(57,39,0,0.35)' },
                            }}>
                            {sending ? (
                                <CircularProgress size={18} sx={{ color: CREATOR_ON_GOLD }} />
                            ) : (
                                <SendOutlinedIcon sx={{ fontSize: 20 }} />
                            )}
                        </IconButton>
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderTop: `1px solid ${BRAND_OUTLINE_VARIANT}33`,
                        bgcolor: BRAND_SURFACE_CONTAINER,
                    }}>
                    {composerDisabled ? (
                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', mb: 1, px: 0.5 }}>
                            {workspace && !belongsToWorkspace
                                ? 'You do not have access to send messages in this workspace.'
                                : participationBlocked
                                ? 'This campaign is closed. You can still read the thread, but messaging is disabled.'
                                : 'Messaging is paused for this campaign.'}
                        </Typography>
                    ) : null}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            opacity: composerDisabled ? 0.5 : 1,
                        }}>
                        <Tooltip
                            title={composerDisabled ? 'Messaging disabled' : 'Attach file or location'}
                            placement="top">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={composerDisabled}
                                    onClick={e => setAttachAnchor(e.currentTarget)}
                                    sx={{
                                        color: attachMenuOpen ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.35)',
                                        '&:hover': { color: BRAND_LUXE_PRIMARY },
                                    }}>
                                    <AttachFileOutlinedIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={
                                composerDisabled
                                    ? 'Messaging disabled for this campaign'
                                    : `Message ${creatorDisplayName}…`
                            }
                            value={draft}
                            onChange={e => {
                                const value = e.target.value;
                                setDraft(value);
                                emitTyping(value.length > 0);
                            }}
                            onKeyDown={handleKeyDown}
                            inputRef={composerInputRef}
                            disabled={!participantId || composerDisabled}
                            multiline
                            maxRows={4}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    borderRadius: 999,
                                    color: '#fff',
                                    fontSize: 14,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '& fieldset': { border: 'none' },
                                    '&:hover fieldset': { border: 'none' },
                                    '&.Mui-focused fieldset': {
                                        border: '1px solid rgba(236,170,0,0.35)',
                                    },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: 'rgba(255,255,255,0.35)',
                                    opacity: 1,
                                },
                            }}
                        />
                        <IconButton
                            disabled={!canSend}
                            onClick={send}
                            size="small"
                            aria-label="Send message"
                            sx={{ color: canSend ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.2)' }}>
                            {sending ? (
                                <CircularProgress size={22} color="inherit" />
                            ) : (
                                <SendOutlinedIcon sx={{ fontSize: 20 }} />
                            )}
                        </IconButton>
                    </Box>
                </Box>
            )}
        </Box>
    );
}

function ThreadErrorAlert({ error }: { error: unknown }) {
    const e = error as { response?: { data?: { message?: string } }; message?: string };
    const msg = e?.response?.data?.message || e?.message || 'Something went wrong';
    return (
        <Alert severity="error" sx={{ py: 0, fontSize: 13 }}>
            {msg}
        </Alert>
    );
}

function MessageBubble({
    message,
    isMine,
    variant,
    contentSubmissions,
    participantId,
    campaignId,
}: {
    message: IWorkspaceMessage;
    isMine: boolean;
    variant: WorkspaceDiscussionVariant;
    contentSubmissions?: IWorkspaceContentSubmission[];
    participantId?: string;
    campaignId?: string;
}) {
    const dispatch = useDispatch();
    const retrying = useSelector(retryingSubmissionProcessingSelector);
    const label = senderLabel(message);
    const attachments = message.attachments ?? [];

    // If this is a submission message, find the matching submission's projects
    const submissionMatch = message.content?.match(/^Submitted work for "(.+)"$/);
    const matchedSubmission = submissionMatch
        ? (contentSubmissions ?? []).find(s => s.deliverable_type === submissionMatch[1])
        : undefined;
    const submissionProjects: ISubmissionProject[] = matchedSubmission?.projects ?? [];

    const canResubmit = variant === 'creator' && isMine && !!matchedSubmission?._id && !!participantId;

    const onRetry = canResubmit
        ? () => {
              dispatch(
                  retrySubmissionProcessing({
                      participantId: participantId!,
                      submissionId: matchedSubmission!._id!,
                      campaignId: campaignId ?? undefined,
                  }),
              );
          }
        : undefined;

    const onReplace =
        canResubmit && !!campaignId
            ? (files: File[]) => {
                  dispatch(
                      submitParticipantWork({
                          participantId: participantId!,
                          campaignId: campaignId!,
                          deliverableType: matchedSubmission!.deliverable_type ?? '',
                          description: '',
                          files,
                          submissionId: matchedSubmission!._id,
                          navigateAfter: false,
                      }),
                  );
              }
            : undefined;

    if (variant === 'creator') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isMine ? 'flex-end' : 'flex-start',
                    gap: 0.5,
                    maxWidth: { xs: '90%', md: '85%' },
                    alignSelf: isMine ? 'flex-end' : 'flex-start',
                }}>
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        bgcolor: isMine ? CREATOR_GOLD : CREATOR_SURFACE,
                        color: isMine ? CREATOR_ON_GOLD : 'rgba(255,255,255,0.9)',
                        border: isMine ? 'none' : '1px solid rgba(255,255,255,0.05)',
                        borderTopLeftRadius: isMine ? 16 : 4,
                        borderTopRightRadius: isMine ? 4 : 16,
                        boxShadow: isMine ? '0 8px 24px rgba(0,0,0,0.25)' : 'none',
                    }}>
                    <CampaignThreadMessageContent
                        content={message.content}
                        attachments={attachments}
                        variant="creator"
                        isMine={isMine}
                    />
                    <CampaignThreadAttachmentGrid attachments={attachments} isMine={isMine} variant="creator" />
                    <SubmissionVideoTiles
                        projects={submissionProjects}
                        isMine={isMine}
                        onRetry={onRetry}
                        onReplace={onReplace}
                        retrying={retrying}
                    />
                </Box>
                <Typography sx={{ fontSize: 10, fontFamily: FONT_ENUM.MAIN_MEDIUM, color: ON_VARIANT_SOFT, mx: 0.5 }}>
                    {formatTimeCreator(message.createdAt)}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isMine ? 'flex-end' : 'flex-start',
                alignSelf: isMine ? 'flex-end' : 'flex-start',
                maxWidth: '80%',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 11,
                        color: isMine ? BRAND_LUXE_PRIMARY : BRAND_LUXE_SECONDARY,
                    }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: 10, fontFamily: FONT_ENUM.MAIN_MEDIUM, color: 'rgba(255,255,255,0.25)' }}>
                    {formatTimeBrand(message.createdAt)}
                </Typography>
            </Box>
            <Box
                sx={{
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: isMine ? 'rgba(255,201,101,0.08)' : BRAND_SURFACE_CONTAINER_HIGHEST,
                    borderBottomRightRadius: isMine ? 4 : undefined,
                    borderBottomLeftRadius: !isMine ? 4 : undefined,
                }}>
                <CampaignThreadMessageContent
                    content={message.content}
                    attachments={attachments}
                    variant="brand"
                    isMine={isMine}
                />
                <CampaignThreadAttachmentGrid attachments={attachments} isMine={isMine} variant="brand" />
                <SubmissionVideoTiles projects={submissionProjects} isMine={isMine} />
            </Box>
        </Box>
    );
}

function SubmissionVideoTiles({
    projects,
    isMine,
    onRetry,
    onReplace,
    retrying,
}: {
    projects: ISubmissionProject[];
    isMine: boolean;
    onRetry?: () => void;
    onReplace?: (files: File[]) => void;
    retrying?: boolean;
}) {
    const [videoModalUrl, setVideoModalUrl] = useState<string | null>(null);
    const replaceInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, cb?: (files: File[]) => void) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        if (files.length) cb?.(files);
        e.target.value = '';
    };

    if (projects.length === 0) return null;

    return (
        <>
            {onReplace && (
                <input
                    ref={replaceInputRef}
                    type="file"
                    accept="video/*"
                    style={{ display: 'none' }}
                    onChange={e => handleFileChange(e, onReplace)}
                />
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                {projects.map((proj, i) => {
                    const videoUrl = proj.download_link ?? proj.hls ?? null;
                    const poster = proj.feature_image ?? proj.thumbnail ?? undefined;
                    const ready = proj.processing_complete !== false;

                    return (
                        <Box
                            key={proj._id ?? i}
                            onClick={() => ready && videoUrl && setVideoModalUrl(videoUrl)}
                            sx={{
                                position: 'relative',
                                width: 120,
                                height: 120,
                                borderRadius: 2,
                                overflow: 'hidden',
                                bgcolor: 'rgba(0,0,0,0.3)',
                                cursor: ready && videoUrl ? 'pointer' : 'default',
                                border: `1px solid ${isMine ? 'rgba(57,39,0,0.2)' : 'rgba(255,255,255,0.08)'}`,
                                flexShrink: 0,
                                '&:hover .play-btn': ready && videoUrl ? { opacity: 1 } : {},
                            }}>
                            {poster ? (
                                <Box
                                    component="img"
                                    src={poster}
                                    alt=""
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <InsertDriveFileOutlinedIcon
                                        sx={{ fontSize: 32, color: 'rgba(255,255,255,0.3)' }}
                                    />
                                </Box>
                            )}
                            {!ready ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(0,0,0,0.55)',
                                        gap: 0.5,
                                    }}>
                                    <CircularProgress size={18} thickness={3} sx={{ color: CREATOR_GOLD }} />
                                    <Typography
                                        sx={{
                                            fontSize: 9,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: 'rgba(255,255,255,0.5)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.06em',
                                        }}>
                                        Processing
                                    </Typography>
                                    {(onRetry || onReplace) && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: '10px',
                                                display: 'flex',
                                                gap: 0.25,
                                            }}>
                                            {onRetry && (
                                                <Tooltip title="Retry processing">
                                                    <IconButton
                                                        size="small"
                                                        disabled={retrying}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            onRetry();
                                                        }}
                                                        sx={{
                                                            color: 'rgba(255,255,255,0.7)',
                                                            p: 0.25,
                                                            '&:hover': {
                                                                color: '#fff',
                                                                bgcolor: 'rgba(255,255,255,0.1)',
                                                            },
                                                            '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
                                                        }}>
                                                        <RefreshIcon
                                                            sx={{
                                                                fontSize: 20,
                                                                animation: retrying
                                                                    ? 'spin 1s linear infinite'
                                                                    : 'none',
                                                                '@keyframes spin': {
                                                                    '0%': { transform: 'rotate(0deg)' },
                                                                    '100%': { transform: 'rotate(360deg)' },
                                                                },
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {onReplace && (
                                                <Tooltip title="Upload different video">
                                                    <IconButton
                                                        size="small"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            replaceInputRef.current?.click();
                                                        }}
                                                        sx={{
                                                            color: 'rgba(255,255,255,0.7)',
                                                            p: 0.25,
                                                            '&:hover': {
                                                                color: '#fff',
                                                                bgcolor: 'rgba(255,255,255,0.1)',
                                                            },
                                                        }}>
                                                        <UploadFileOutlinedIcon sx={{ fontSize: 20 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Box
                                    className="play-btn"
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(0,0,0,0.3)',
                                        opacity: 0.8,
                                        transition: 'opacity 0.15s',
                                    }}>
                                    <PlayCircleOutlineIcon sx={{ fontSize: 40, color: '#fff' }} />
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>

            <Dialog
                open={Boolean(videoModalUrl)}
                onClose={() => setVideoModalUrl(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { bgcolor: '#000', p: 0, borderRadius: 2, overflow: 'hidden' } }}>
                {videoModalUrl && (
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        <Box
                            component="video"
                            src={videoModalUrl}
                            controls
                            autoPlay
                            sx={{ width: '100%', display: 'block', outline: 'none', maxHeight: '80vh' }}
                        />
                        <IconButton
                            onClick={() => setVideoModalUrl(null)}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                color: '#fff',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                            }}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                )}
            </Dialog>
        </>
    );
}
